import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { http } from '@/network/http';

export interface RssFeed {
  id: string;
  name: string;
  url: string;
  iconUrl: string | null;
  isEnabled: boolean;
  isBreaking: boolean;
  maxPostsPerHour: number;
  lastSyncAt: string | null;
  syncError: string | null;
  notifyTier: string;
  notifyKeywords: string[];
  notifyRoleIds: string[];
  lastDigestAt: string | null;
  lastWeeklyAt: string | null;
  postCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRssFeedData {
  name: string;
  url: string;
  iconUrl?: string | null;
  isEnabled?: boolean;
  isBreaking?: boolean;
  maxPostsPerHour?: number;
  notifyTier?: string;
  notifyKeywords?: string[];
  notifyRoleIds?: string[];
}

export interface UpdateRssFeedData {
  name?: string;
  url?: string;
  iconUrl?: string | null;
  isEnabled?: boolean;
  isBreaking?: boolean;
  maxPostsPerHour?: number;
  notifyTier?: string;
  notifyKeywords?: string[];
  notifyRoleIds?: string[];
}

const KEYS = {
  all: ['rssFeeds'] as const,
  list: () => [...KEYS.all, 'list'] as const,
};

export function useRssFeeds() {
  return useQuery({
    queryKey: KEYS.list(),
    queryFn: async () => {
      const { data } = await http.get('/rss');
      return data.feeds as RssFeed[];
    },
  });
}

export function useCreateRssFeed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateRssFeedData) => {
      const { data: result } = await http.post('/rss', data);
      return result.feed as RssFeed;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateRssFeed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateRssFeedData }) => {
      const { data: result } = await http.put(`/rss/${id}`, data);
      return result.feed as RssFeed;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useDeleteRssFeed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await http.delete(`/rss/${id}`);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useSyncRssFeeds() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (feedId: string | undefined) => {
      const url = feedId ? `/rss/sync?feedId=${feedId}` : '/rss/sync';
      const { data } = await http.post(url);
      return data as { success: boolean; synced?: number; imported?: number; errors?: number };
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function usePurgeRssFeedPosts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (feedId: string) => {
      const { data } = await http.delete(`/rss/${feedId}/posts`);
      return data as { success: boolean; deleted: number };
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useResyncRssFeed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (feedId: string) => {
      const { data } = await http.post(`/rss/resync/${feedId}`);
      return data as { success: boolean; deleted: number; imported: number; error?: string | null };
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}
